// Landing route reached when an admin clicks "Impersonate" in the admin
// app. The admin app same-tab-navigates to:
//   /impersonate?token=<impersonationJWT>&returnToken=<adminReturnJWT>
//
// We drop the impersonation JWT into the app's existing token slot
// (`mathsAuthToken` via setToken), stash the admin-return JWT under a
// separate localStorage key so the banner can hand it back at the end,
// hydrate Redux from the new token, and land the browser on /dashboard.
//
// This route is intentionally NOT wrapped in any auth guard — the token
// in the query string IS the auth.

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import { jwtDecode } from "jwt-decode";

import { setToken, removeToken } from "../../utils";
import { syncDatabaseUser } from "../../store/main";

export const IMPERSONATION_KEYS = {
  returnToken: "adminReturnToken",
  impersonatedBy: "impersonatedBy",
};

export default function ImpersonateLanding() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const returnToken = params.get("returnToken");

    if (!token || !returnToken) {
      setError("Missing impersonation tokens.");
      setTimeout(() => navigate("/", { replace: true }), 2000);
      return;
    }

    try {
      // Wipe any previously-logged-in user's token before installing the
      // impersonation one, so we don't leak that user's Redux state during
      // syncDatabaseUser.
      removeToken();

      setToken(token);
      localStorage.setItem(IMPERSONATION_KEYS.returnToken, returnToken);
      try {
        const decoded = jwtDecode(token);
        if (decoded.impersonatedBy) {
          localStorage.setItem(
            IMPERSONATION_KEYS.impersonatedBy,
            String(decoded.impersonatedBy)
          );
        }
      } catch (_) {
        /* JWT decode failure is non-fatal — the banner just won't show the
           "impersonated by" attribution, which is optional anyway. */
      }

      // Hydrate Redux from the new token, then land on the dashboard.
      dispatch(syncDatabaseUser());
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("ImpersonateLanding error", err);
      setError("Something went wrong starting the impersonation session.");
      setTimeout(() => navigate("/", { replace: true }), 2500);
    }
  }, [dispatch, location.search, navigate]);

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 3,
        textAlign: "center",
      }}
    >
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Starting impersonation session…
          </Typography>
        </>
      )}
    </Box>
  );
}
